/**
 * Instanced ground cover (E3.3.S5, AD-159) — the PURE, node-safe core. Like
 * plan.ts/budget.ts it imports NO `three`: it decides WHERE grass clumps stand,
 * HOW MANY a tier may draw, and the wind maths the vertex shader mirrors
 * (`groundCoverMesh.ts` realizes it; `windGlsl()` below is generated from the
 * same constants, so the shader and the reference cannot drift apart).
 *
 * Presentation only (AD-044/047): no collider, no interaction, no content
 * binding, no sim read — a clump is a render instance and nothing else. The
 * biome → cover table is a client presentation convention keyed on the ground
 * PALETTE (like `BIOME_GROUND_PALETTE`), never a content field: new content
 * grammar is Fable-reserved, and ground cover needs none.
 *
 * Wind model: ported MATHS from stylized-scene `src/materials/wind.ts`
 * (Andre Elias / dedekpo, MIT — see `THIRD_PARTY_NOTICE.txt` beside this file).
 * The reference is WebGPU + TSL; nothing of its material graph is used — only
 * the wave equations, re-expressed here and in GLSL (AD-159 §"What IS adaptable").
 *
 * Import boundary (AD-122): frontend-local; no shared/server imports.
 */

import type { RenderTier } from './budget.js';
import type { ScenePlan } from './plan.js';
import { onIslandTop } from './island.js';
import { heightfieldHeightAt } from './terrain.js';

// ---------------------------------------------------------------------------
// Which grounds grow cover, and in what colours.
// ---------------------------------------------------------------------------

/** One cover style: blade colours (sRGB hex) + how dense and patchy it grows. */
export interface GroundCoverStyle {
  readonly id: string;
  /** Blade colour at the base (before the ground projection) and at the tip. */
  readonly base: string;
  readonly tip: string;
  /** Per-clump hue/value spread, 0..1 (0 = every clump identical). */
  readonly variation: number;
  /** Fraction of the ground the cover patches occupy (the low-frequency mask), 0..1. */
  readonly coverage: number;
  /** Blade height range, metres. */
  readonly heightMin: number;
  readonly heightMax: number;
}

/**
 * Ground palette id → cover style. A palette not listed grows nothing (towns,
 * canyon grit, the mire, the neutral sparring flat): the zone's plan is drawn
 * exactly as before. First biomes (the E3 exit criterion's "at least one"):
 * the Measured Yard / Starlight Meadows (`open-meadow`) and the Verdant Reach
 * (`verdant-forest`).
 */
export const GROUND_COVER_BY_PALETTE: Readonly<Record<string, GroundCoverStyle>> = {
  'open-meadow': {
    id: 'meadow-grass',
    base: '#4d6b35',
    tip: '#a7b865',
    variation: 0.35,
    coverage: 0.62,
    heightMin: 0.35,
    heightMax: 0.7,
  },
  'verdant-forest': {
    id: 'forest-grass',
    base: '#3c5f2e',
    tip: '#86a552',
    variation: 0.3,
    coverage: 0.5,
    heightMin: 0.3,
    heightMax: 0.6,
  },
};

/** The cover style a plan's palette grows, or null (⇒ no ground cover). */
export function groundCoverStyleFor(palette: string | null | undefined): GroundCoverStyle | null {
  if (!palette) return null;
  return GROUND_COVER_BY_PALETTE[palette] ?? null;
}

// ---------------------------------------------------------------------------
// Per-tier budget. A clump is ONE instance of one blade-clump geometry; the
// geometry is fixed per tier, so triangles = clumps × trianglesPerClump.
// ---------------------------------------------------------------------------

export interface GroundCoverTierBudget {
  /** Clumps per square metre of eligible ground (before the coverage mask). */
  readonly density: number;
  /** Hard cap on clumps for the whole zone. */
  readonly maxClumps: number;
  /** Blades per clump (geometry detail). */
  readonly bladesPerClump: number;
  /** Height segments per blade (a pointed tip closes the last one). */
  readonly segments: number;
  /** Chunks beyond this camera distance (to the chunk's near edge) are hidden. */
  readonly cullDistance: number;
}

export const GROUND_COVER_TIERS: Readonly<Record<RenderTier, GroundCoverTierBudget>> = {
  desktop: { density: 1.4, maxClumps: 24_000, bladesPerClump: 7, segments: 4, cullDistance: 75 },
  mobile: { density: 0.45, maxClumps: 6_000, bladesPerClump: 4, segments: 3, cullDistance: 40 },
};

/** Chunk edge, metres: one InstancedMesh (= one draw call) per non-empty chunk. */
export const GROUND_COVER_CHUNK_METRES = 24;

/** Triangles in one blade: 2 per full segment, 1 for the pointed tip. */
export function bladeTriangles(segments: number): number {
  return 2 * (segments - 1) + 1;
}

export function clumpTriangles(budget: GroundCoverTierBudget): number {
  return budget.bladesPerClump * bladeTriangles(budget.segments);
}

// ---------------------------------------------------------------------------
// Placement — deterministic jittered grid under a patchy coverage mask.
// ---------------------------------------------------------------------------

/** A circle grass must not grow in (a building, a marker, a tree trunk). */
export interface CoverExclusion {
  x: number;
  z: number;
  radius: number;
}

export interface GroundCoverInput {
  /** Ground rectangle (the plan's `ground`: centre + size). */
  x: number;
  z: number;
  sizeX: number;
  sizeZ: number;
  palette: string | null | undefined;
  exclusions: readonly CoverExclusion[];
  /** Ground height at (x, z) — the SAME surface the ground mesh is drawn from. */
  heightAt: (x: number, z: number) => number;
  /** AD-840: on an island, only where this holds (the top, short of the lip); absent ⇒ everywhere. */
  keep?: (x: number, z: number) => boolean;
}

/** One clump instance. `tint` and `phase` are 0..1 and ride instance attributes. */
export interface CoverClump {
  x: number;
  y: number;
  z: number;
  rotationY: number;
  height: number;
  tint: number;
  phase: number;
}

export interface CoverChunk {
  /** Chunk centre (x, z) and its half-diagonal, for the distance cull. */
  cx: number;
  cz: number;
  radius: number;
  clumps: CoverClump[];
}

export interface GroundCoverPlan {
  style: GroundCoverStyle;
  tier: RenderTier;
  chunks: CoverChunk[];
  clumpCount: number;
}

/** A 32-bit integer hash of two ints + a seed (xxhash-style avalanche). */
function hash2(ix: number, iz: number, seed: number): number {
  let h = (seed ^ Math.imul(ix, 0x27d4eb2d) ^ Math.imul(iz, 0x165667b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** Hash → [0, 1). */
function unit(ix: number, iz: number, seed: number): number {
  return hash2(ix, iz, seed) / 4294967296;
}

function stringSeed(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619) >>> 0;
  return h;
}

/** Smooth 2D value noise in [0, 1) — the patch mask (bare ground between meadows). */
export function valueNoise(x: number, z: number, seed: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  const a = unit(ix, iz, seed);
  const b = unit(ix + 1, iz, seed);
  const c = unit(ix, iz + 1, seed);
  const d = unit(ix + 1, iz + 1, seed);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}

/** Cell edge of the exclusion lookup grid, metres. */
const EXCLUSION_CELL = 8;

/**
 * A point-in-any-circle test over a uniform grid: each circle is filed under
 * every cell its bounding box touches, so a query reads one cell. A dressed
 * zone carries hundreds of keep-outs against tens of thousands of candidates.
 */
function exclusionTest(exclusions: readonly CoverExclusion[]): (x: number, z: number) => boolean {
  const grid = new Map<string, CoverExclusion[]>();
  for (const e of exclusions) {
    const x0 = Math.floor((e.x - e.radius) / EXCLUSION_CELL);
    const x1 = Math.floor((e.x + e.radius) / EXCLUSION_CELL);
    const z0 = Math.floor((e.z - e.radius) / EXCLUSION_CELL);
    const z1 = Math.floor((e.z + e.radius) / EXCLUSION_CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const key = `${cx},${cz}`;
        const list = grid.get(key);
        if (list) list.push(e);
        else grid.set(key, [e]);
      }
    }
  }
  return (x, z) => {
    const list = grid.get(`${Math.floor(x / EXCLUSION_CELL)},${Math.floor(z / EXCLUSION_CELL)}`);
    return list !== undefined && list.some((e) => Math.hypot(x - e.x, z - e.z) < e.radius);
  };
}

/** Patch size of the coverage mask, metres. */
const PATCH_METRES = 9;

/**
 * Plan the zone's ground cover for a tier, or null when the palette grows none.
 * Pure and deterministic: same input + tier ⇒ same clumps (the lane films the
 * same meadow every run). Spacing widens to honour `maxClumps` on a big zone —
 * density yields, the budget never does.
 */
export function planGroundCover(input: GroundCoverInput, tier: RenderTier): GroundCoverPlan | null {
  const style = groundCoverStyleFor(input.palette);
  if (style === null) return null;
  const budget = GROUND_COVER_TIERS[tier];
  const area = input.sizeX * input.sizeZ;
  if (!(area > 0)) return null;
  // Candidates are drawn on the full rectangle, then thinned by the coverage
  // mask, so the expected count is area × density × coverage.
  const spacing = Math.max(
    1 / Math.sqrt(budget.density),
    Math.sqrt((area * style.coverage) / budget.maxClumps),
  );
  const seed = stringSeed(`${style.id}:${input.x}:${input.z}:${input.sizeX}:${input.sizeZ}`);
  const minX = input.x - input.sizeX / 2;
  const minZ = input.z - input.sizeZ / 2;
  const cols = Math.floor(input.sizeX / spacing);
  const rows = Math.floor(input.sizeZ / spacing);
  const chunkCols = Math.max(1, Math.ceil(input.sizeX / GROUND_COVER_CHUNK_METRES));
  const chunkRows = Math.max(1, Math.ceil(input.sizeZ / GROUND_COVER_CHUNK_METRES));
  const buckets: CoverClump[][] = Array.from({ length: chunkCols * chunkRows }, () => []);
  const excluded = exclusionTest(input.exclusions);
  let count = 0;
  for (let r = 0; r < rows && count < budget.maxClumps; r++) {
    for (let c = 0; c < cols && count < budget.maxClumps; c++) {
      const x = minX + (c + 0.1 + 0.8 * unit(c, r, seed)) * spacing;
      const z = minZ + (r + 0.1 + 0.8 * unit(c, r, seed ^ 0x9e3779b9)) * spacing;
      // The mask: a clump grows where the patch noise clears the coverage threshold.
      if (valueNoise(x / PATCH_METRES, z / PATCH_METRES, seed ^ 0x51ed270b) > style.coverage) {
        continue;
      }
      if (excluded(x, z)) continue;
      if (input.keep && !input.keep(x, z)) continue;
      const h = unit(c, r, seed ^ 0x2545f491);
      const clump: CoverClump = {
        x,
        y: input.heightAt(x, z),
        z,
        rotationY: unit(c, r, seed ^ 0x68e31da4) * Math.PI * 2,
        height: style.heightMin + (style.heightMax - style.heightMin) * h,
        tint: unit(c, r, seed ^ 0x1b873593),
        phase: unit(c, r, seed ^ 0x3c6ef372),
      };
      const kc = Math.min(chunkCols - 1, Math.floor((x - minX) / GROUND_COVER_CHUNK_METRES));
      const kr = Math.min(chunkRows - 1, Math.floor((z - minZ) / GROUND_COVER_CHUNK_METRES));
      (buckets[kr * chunkCols + kc] as CoverClump[]).push(clump);
      count += 1;
    }
  }
  const half = GROUND_COVER_CHUNK_METRES / 2;
  const chunks: CoverChunk[] = [];
  buckets.forEach((clumps, i) => {
    if (clumps.length === 0) return;
    const kc = i % chunkCols;
    const kr = Math.floor(i / chunkCols);
    chunks.push({
      cx: minX + kc * GROUND_COVER_CHUNK_METRES + half,
      cz: minZ + kr * GROUND_COVER_CHUNK_METRES + half,
      radius: Math.SQRT2 * half,
      clumps,
    });
  });
  return { style, tier, chunks, clumpCount: count };
}

/** AD-840: grass stops this far short of an island's lip, metres. */
export const COVER_RIM_INSET_M = 0.5;

/** Keep-out radii, metres: grass never grows through a building floor, a marker or a trunk. */
export const COVER_KEEP_OUT = {
  marker: 2.5,
  settlementPiece: 3.5,
  /** × the placement's scale. */
  scatterDressing: 0.8,
  rock: 1.2,
  assetPlacement: 4,
} as const;

/**
 * The cover input a scene plan implies: its ground rectangle, palette and
 * surface, with every drawn object carved out. Pure — the plan already holds
 * everything, so the cover needs no field of its own on `ScenePlan`.
 */
export function coverInputFromPlan(plan: ScenePlan): GroundCoverInput {
  const g = plan.ground;
  const hf = g.heightfield;
  const island = plan.island;
  const exclusions: CoverExclusion[] = [
    ...plan.markers.map((m) => ({ x: m.x, z: m.z, radius: COVER_KEEP_OUT.marker })),
    ...plan.props.map((p) => ({ x: p.x, z: p.z, radius: COVER_KEEP_OUT.rock * p.rock.scale })),
    ...plan.assetPlacements.map((a) => ({
      x: a.x,
      z: a.z,
      radius: COVER_KEEP_OUT.assetPlacement * a.scale,
    })),
    ...plan.dressing.map((d) => ({
      x: d.x,
      z: d.z,
      radius:
        d.settlementId !== null
          ? COVER_KEEP_OUT.settlementPiece
          : COVER_KEEP_OUT.scatterDressing * d.scale,
    })),
  ];
  return {
    x: g.x,
    z: g.z,
    sizeX: g.sizeX,
    sizeZ: g.sizeZ,
    palette: g.palette,
    exclusions,
    heightAt: hf ? (x, z) => heightfieldHeightAt(hf, x, z) : () => 0,
    ...(island
      ? { keep: (x: number, z: number) => onIslandTop(island, x, z, COVER_RIM_INSET_M) }
      : {}),
  };
}

/** Whether a chunk is drawn from a camera at (x, z): its NEAR edge within the tier's cull distance. */
export function chunkVisible(
  chunk: CoverChunk,
  cameraX: number,
  cameraZ: number,
  tier: RenderTier,
): boolean {
  const dist = Math.hypot(chunk.cx - cameraX, chunk.cz - cameraZ) - chunk.radius;
  return dist <= GROUND_COVER_TIERS[tier].cullDistance;
}

/** Worst-case per-frame cost of a cover plan (every chunk visible) — the budget estimate's input. */
export function groundCoverCost(plan: GroundCoverPlan): { drawCalls: number; triangles: number } {
  return {
    drawCalls: plan.chunks.length,
    triangles: plan.clumpCount * clumpTriangles(GROUND_COVER_TIERS[plan.tier]),
  };
}

// ---------------------------------------------------------------------------
// Wind — the AD-159 model: a travelling gust wave + a constant breeze floor +
// mid-frequency chop, bending each blade along a circular arc, plus tip flutter.
// ---------------------------------------------------------------------------

export interface WindParams {
  /** Horizontal direction the wind blows toward (normalized by the consumer). */
  dirX: number;
  dirZ: number;
  /** Overall strength (scales the bend angle). */
  strength: number;
  /** Travel speed of the gust front. */
  speed: number;
  /** Spatial frequency of the gust front along the wind axis (1/m). */
  gustScale: number;
  /** The breeze that never stops: bend intensity with no gust present. */
  calmFloor: number;
  /** Weight of the mid-frequency chop. */
  chopStrength: number;
  /** Tip flutter amplitude (fraction of blade height, before its 0.08 scale). */
  flutter: number;
  /** Arc exponent: > 1 keeps the base stiff and bends the tip. */
  bendExponent: number;
}

export const WIND_DEFAULTS: Readonly<WindParams> = {
  dirX: 0.8,
  dirZ: 0.6,
  strength: 0.25,
  speed: 1.2,
  gustScale: 0.12,
  calmFloor: 0.25,
  chopStrength: 0.18,
  flutter: 1,
  bendExponent: 1.5,
};

/** Hard ceiling on the bend angle (radians) — past ~92° a blade folds into the ground. */
export const MAX_BEND = 1.6;

/** The gust wave's shaped amplitude in [0, 1] at a point along the wind axis. */
export function gustAt(
  along: number,
  time: number,
  jitter: number,
  w: WindParams = WIND_DEFAULTS,
): number {
  const phase = along * w.gustScale - time * w.speed * 0.6 + jitter * 1.5;
  return Math.pow(Math.sin(phase) * 0.5 + 0.5, 1.6);
}

/** Total bend angle (radians) of a clump at `along` with its own `phase` (0..1). */
export function bendAngle(
  along: number,
  time: number,
  phase: number,
  jitter: number,
  w: WindParams = WIND_DEFAULTS,
): number {
  const gust = gustAt(along, time, jitter, w);
  const chop =
    Math.sin(along * w.gustScale * 2.7 - time * w.speed * 1.3 + phase * Math.PI * 2) * 0.5 + 0.5;
  const ampVar = 0.8 + 0.4 * phase;
  const intensity = (w.calmFloor + gust * 0.85 + chop * w.chopStrength) * ampVar;
  return Math.min(MAX_BEND, Math.max(0, w.strength * intensity * 3));
}

/**
 * Circular-arc bend of a blade point at normalized height `t` (0 base … 1 tip)
 * on a blade `height` metres tall bent by total angle `phi`: how far it moves
 * along the wind (`advance`) and how far it drops (`drop`, ≤ 0). The blade keeps
 * its length (an arc, not a shear), so a hard gust lays it down rather than
 * stretching it.
 */
export function arcBend(
  t: number,
  height: number,
  phi: number,
  w: WindParams = WIND_DEFAULTS,
): { advance: number; drop: number } {
  const p = Math.max(phi, 1e-4);
  const a = p * Math.pow(t, w.bendExponent);
  const radius = height / p;
  return { advance: radius * (1 - Math.cos(a)), drop: radius * Math.sin(a) - t * height };
}

/** Tip flutter: a fast sideways flick confined to the top of the blade (fraction of height). */
export function tipFlutter(
  t: number,
  time: number,
  phase: number,
  along: number,
  w: WindParams = WIND_DEFAULTS,
): number {
  const edge = Math.min(1, Math.max(0, (t - 0.55) / 0.45));
  const smooth = edge * edge * (3 - 2 * edge);
  return Math.sin(time * 10 + phase * 3 + along * 0.8) * w.flutter * 0.08 * smooth;
}

function glslFloat(n: number): string {
  const s = String(n);
  return s.includes('.') || s.includes('e') ? s : `${s}.0`;
}

/**
 * The shader constants a `WindParams` bakes into the grass vertex stage — ONE derivation
 * both siblings read: `windGlsl()` prints them, groundCoverNodeMaterial.ts (the WebGPU / TSL
 * sibling, q-webgpu-grass-and-flame-parity) builds its graph from them. Names follow the
 * GLSL they land in.
 */
export interface WindCoefficients {
  /** `gcBendAngle`: gust = pow(sin(along·gustScale − time·gustSpeed + jitter·1.5)·0.5 + 0.5, 1.6). */
  readonly gustScale: number;
  readonly gustSpeed: number;
  /** chop = sin(along·chopScale − time·chopSpeed + phase·2π)·0.5 + 0.5. */
  readonly chopScale: number;
  readonly chopSpeed: number;
  readonly calmFloor: number;
  readonly chopStrength: number;
  /** angle = clamp(strength·intensity·3, 0, maxBend). */
  readonly strength: number;
  readonly maxBend: number;
  /** `gcArcBend`: a = phi·pow(t, bendExponent). */
  readonly bendExponent: number;
  /** `gcTipFlutter`: sin(time·10 + phase·3 + along·0.8)·flutter·smoothstep(0.55, 1, t). */
  readonly flutter: number;
}

export function windCoefficients(w: WindParams = WIND_DEFAULTS): WindCoefficients {
  return {
    gustScale: w.gustScale,
    gustSpeed: w.speed * 0.6,
    chopScale: w.gustScale * 2.7,
    chopSpeed: w.speed * 1.3,
    calmFloor: w.calmFloor,
    chopStrength: w.chopStrength,
    strength: w.strength,
    maxBend: MAX_BEND,
    bendExponent: w.bendExponent,
    flutter: w.flutter * 0.08,
  };
}

/**
 * The GLSL twin of `bendAngle` / `arcBend` / `tipFlutter`, with the tuning
 * constants baked in from `w`. Returns function definitions only; the material
 * injects them and supplies `uGcTime` + `uGcWindDir`. `gcJitter` replaces the
 * reference's noise texture with a hashed value noise (no texture fetch in the
 * vertex stage, which is what keeps it cheap on the mobile tier); the hash is the
 * sin-free Hoskins hash12 groundMaterial.ts uses, for the same mediump reason.
 */
export function windGlsl(w: WindParams = WIND_DEFAULTS): string {
  const f = glslFloat;
  const k = windCoefficients(w);
  return `
float gcHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float gcJitter(vec2 p) {
  vec2 i = floor(p); vec2 fr = fract(p); vec2 s = fr * fr * (3.0 - 2.0 * fr);
  float a = gcHash(i); float b = gcHash(i + vec2(1.0, 0.0));
  float c = gcHash(i + vec2(0.0, 1.0)); float d = gcHash(i + vec2(1.0, 1.0));
  return (mix(mix(a, b, s.x), mix(c, d, s.x), s.y) - 0.5) * 2.0;
}
float gcBendAngle(float along, float time, float phase, float jitter) {
  float gust = pow(sin(along * ${f(k.gustScale)} - time * ${f(k.gustSpeed)} + jitter * 1.5) * 0.5 + 0.5, 1.6);
  float chop = sin(along * ${f(k.chopScale)} - time * ${f(k.chopSpeed)} + phase * 6.28318530718) * 0.5 + 0.5;
  float intensity = (${f(k.calmFloor)} + gust * 0.85 + chop * ${f(k.chopStrength)}) * (0.8 + 0.4 * phase);
  return clamp(${f(k.strength)} * intensity * 3.0, 0.0, ${f(k.maxBend)});
}
vec2 gcArcBend(float t, float height, float phi) {
  float p = max(phi, 1e-4);
  float a = p * pow(t, ${f(k.bendExponent)});
  float radius = height / p;
  return vec2(radius * (1.0 - cos(a)), radius * sin(a) - t * height);
}
float gcTipFlutter(float t, float time, float phase, float along) {
  return sin(time * 10.0 + phase * 3.0 + along * 0.8) * ${f(k.flutter)} * smoothstep(0.55, 1.0, t);
}
`;
}
